import React, { useState } from 'react';
import { GripVertical, FileSignature, Sparkles, CalendarClock, Mail, Globe, Send } from 'lucide-react';

const STATUS_COLUMNS = [
  { value: 'new',         label: 'Neu',            color: 'border-slate-300',  bg: 'bg-slate-50',   pill: 'bg-slate-100 text-slate-700'  },
  { value: 'researched',  label: 'Recherchiert',   color: 'border-blue-300',   bg: 'bg-blue-50/40', pill: 'bg-blue-50 text-blue-700'     },
  { value: 'letter_sent', label: 'Brief versendet',color: 'border-amber-300',  bg: 'bg-amber-50/40',pill: 'bg-amber-50 text-amber-700'   },
  { value: 'follow_up',   label: 'Nachfass',       color: 'border-orange-300', bg: 'bg-orange-50/40',pill:'bg-orange-50 text-orange-700' },
  { value: 'meeting',     label: 'Erstgespräch',   color: 'border-purple-300', bg: 'bg-purple-50/40',pill:'bg-purple-50 text-purple-700' },
  { value: 'mandate',     label: 'Mandat',         color: 'border-green-400',  bg: 'bg-green-50/60', pill: 'bg-green-100 text-green-800' },
  { value: 'closed',      label: 'Abgeschlossen',  color: 'border-slate-300',  bg: 'bg-slate-50',   pill: 'bg-slate-100 text-slate-500'  },
];

const PipelineKanban = ({ targets, onChangeStatus, onOpenLead }) => {
  const [dragId, setDragId] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);

  const todayStr = new Date().toISOString().slice(0, 10);

  const websiteHref = (url) => {
    const u = (url || '').trim();
    if (!u) return '';
    return /^https?:\/\//i.test(u) ? u : `https://${u}`;
  };

  const grouped = STATUS_COLUMNS.reduce((acc, col) => {
    acc[col.value] = targets.filter((t) => (t.status || 'new') === col.value);
    return acc;
  }, {});

  const handleDragStart = (e, t) => {
    setDragId(t.id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', t.id);
  };
  const handleDragEnd = () => {
    setDragId(null);
    setHoverCol(null);
  };
  const handleDragOver = (e, status) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (hoverCol !== status) setHoverCol(status);
  };
  const handleDrop = async (e, status) => {
    e.preventDefault();
    const tid = e.dataTransfer.getData('text/plain') || dragId;
    setDragId(null);
    setHoverCol(null);
    if (!tid) return;
    const target = targets.find((t) => t.id === tid);
    if (!target || target.status === status) return;
    await onChangeStatus(target, status);
  };

  return (
    <div className="overflow-x-auto" data-testid="pipeline-kanban">
      <div className="flex gap-3 pb-4 min-w-max">
        {STATUS_COLUMNS.map((col) => {
          const items = grouped[col.value] || [];
          const isHover = hoverCol === col.value && dragId;
          return (
            <div
              key={col.value}
              onDragOver={(e) => handleDragOver(e, col.value)}
              onDrop={(e) => handleDrop(e, col.value)}
              className={`flex flex-col w-[260px] flex-shrink-0 rounded-lg border-2 transition-all ${
                isHover ? 'border-orange-400 bg-orange-50/40' : `${col.color} ${col.bg}`
              }`}
              data-testid={`kanban-col-${col.value}`}
            >
              <div className="px-3 py-2.5 border-b border-slate-200 flex items-center justify-between">
                <span className="text-xs font-semibold uppercase tracking-wider text-slate-700">
                  {col.label}
                </span>
                <span className={`inline-flex items-center justify-center text-[11px] font-bold px-2 py-0.5 rounded-full ${col.pill}`}>
                  {items.length}
                </span>
              </div>
              <div className="p-2 space-y-2 min-h-[100px]">
                {items.length === 0 ? (
                  <p className="text-xs text-slate-400 italic text-center py-3">
                    {dragId ? 'Hier ablegen' : '–'}
                  </p>
                ) : (
                  items.map((t) => {
                    const followUp = (t.follow_up_date || '').trim();
                    const overdue = followUp && followUp < todayStr;
                    const dueToday = followUp && followUp === todayStr;
                    const offerCount = (t.offers || []).length;
                    const briefingCount = (t.briefings || []).length;
                    const activityCount = (t.activities || []).length;
                    const firstContact = (t.first_contact_date || '').trim();
                    let firstContactDays = null;
                    if (firstContact) {
                      const diffMs = new Date(todayStr) - new Date(firstContact);
                      firstContactDays = Math.max(0, Math.round(diffMs / 86400000));
                    }
                    return (
                      <div
                        key={t.id}
                        draggable
                        onDragStart={(e) => handleDragStart(e, t)}
                        onDragEnd={handleDragEnd}
                        onClick={() => onOpenLead(t.id)}
                        className={`bg-white rounded-md border border-slate-200 p-2.5 cursor-grab active:cursor-grabbing hover:shadow-md hover:border-slate-300 transition-all ${
                          dragId === t.id ? 'opacity-40' : ''
                        }`}
                        data-testid={`kanban-card-${t.id}`}
                      >
                        <div className="flex items-start gap-1.5">
                          <GripVertical size={13} className="text-slate-300 mt-0.5 flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start gap-1">
                              <div className="flex-1 min-w-0 font-medium text-sm text-slate-900 truncate" title={t.name}>
                                {t.name}
                              </div>
                              {t.website && (
                                <a
                                  href={websiteHref(t.website)}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  draggable={false}
                                  onClick={(e) => e.stopPropagation()}
                                  onMouseDown={(e) => e.stopPropagation()}
                                  className="flex-shrink-0 p-0.5 rounded hover:bg-orange-50 transition-colors"
                                  title={`Webseite öffnen: ${t.website}`}
                                  data-testid={`kanban-website-${t.id}`}
                                >
                                  <Globe size={12} className="text-slate-400 hover:text-orange-600" />
                                </a>
                              )}
                            </div>
                            <div className="text-[11px] text-slate-500 mt-0.5 truncate">
                              {t.city || t.state}
                              {t.contact_person ? ` · ${t.contact_person}` : ''}
                            </div>
                            {(activityCount > 0 || briefingCount > 0 || offerCount > 0 || followUp || firstContact) && (
                              <div className="flex items-center gap-2 mt-1.5 flex-wrap">
                                {firstContact && (
                                  <span
                                    className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700"
                                    title={`Erstkontakt am ${new Date(firstContact).toLocaleDateString('de-DE')}`}
                                    data-testid={`kanban-firstcontact-${t.id}`}
                                  >
                                    <Send size={9} />
                                    {firstContactDays === 0 ? 'heute' : `${firstContactDays} T.`}
                                  </span>
                                )}
                                {followUp && (
                                  <span
                                    className={`inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded ${
                                      overdue
                                        ? 'bg-red-50 text-red-700'
                                        : dueToday
                                        ? 'bg-amber-50 text-amber-700'
                                        : 'bg-blue-50 text-blue-700'
                                    }`}
                                  >
                                    <CalendarClock size={9} />
                                    {new Date(followUp).toLocaleDateString('de-DE')}
                                  </span>
                                )}
                                {activityCount > 0 && (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-slate-600" title="Aktivitäten">
                                    <Mail size={9} />
                                    {activityCount}
                                  </span>
                                )}
                                {briefingCount > 0 && (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-orange-600" title="Briefings">
                                    <Sparkles size={9} />
                                    {briefingCount}
                                  </span>
                                )}
                                {offerCount > 0 && (
                                  <span className="inline-flex items-center gap-1 text-[10px] text-orange-700 font-medium" title="Angebote">
                                    <FileSignature size={9} />
                                    {offerCount}
                                  </span>
                                )}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default PipelineKanban;
